import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Modal,
  Box,
  Text,
  Group,
  Flex,
  Button,
  Select,
  Radio,
  Table,
  Pagination,
  Badge,
  Paper,
  Skeleton,
  TextInput,
  LoadingOverlay,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import { IconX, IconDownload, IconReceipt, IconSearch } from '@tabler/icons-react';
import { useDebouncedValue } from '@mantine/hooks';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import { notifications } from '@mantine/notifications';
import AggregatedReportsAPI from '../../api/AggregatedReportsApi';
import TestsApi from '../../api/TestsApi';
import { VisiLoader } from '../GeneralComponent/GeneralComponent';
import { SortableTh, SortState } from './sorting';

export type AggregateConversionLogParams = {
  visiopt_campaign_id: string[];
  filter_visiopt_campaign_ids?: string[];
  goal_key: string;
  goal_label: string;
  attribution_model: string;
  // The report's attribution window, so the drawer counts what the row it opened from counted.
  attribute_window_type?: string;
  lookback_days?: number;
  report_time: string;
  // Scope the log was opened from; drives the header text.
  level: 'total' | 'funnel';
  scope_label: string;
  report_id: string;
  traffic_source?: string[];
  // Funnel view only: the log follows the Test Variation toggle the number was counted with.
  test_variation_enabled?: number;
  // Opened from a variation row: the log lists that variation's conversions alone.
  variation_id?: string;
  // Opened from a 0 count whose goal has undefined conversions: land on the Undefined view.
  initial_kind?: 'undefined';
};

type GoalOption = { goal_key: string; goal_name: string };
type LogRow = Record<string, string>;

const NUMBER_PER_PAGE = 15;
const DEFAULT_SORT: SortState = { field: 'date', dir: 'desc' };

// Column label -> row key, so the table renders straight from conversion_field.
const FIELD_TO_KEY: Record<string, string> = {
  'Date': 'date',
  'Traffic Source': 'traffic_source',
  'Visiopt Campaign': 'visiopt_campaign',
  'Ad Campaign': 'ad_campaign',
  'Email': 'email',
  'Transaction ID': 'transaction_id',
  'Sales Amount': 'sales_amount',
  'Subscription Amount': 'subscription_amount',
  'Reason': 'reason',
  'Fingerprint': 'fingerprint',
  'Action': 'action',
  'User': 'user',
  'Conversions': 'conversions',
  'Goal Name': 'goal_name',
};

// Floor widths per column, so a re-sort cannot reflow the table around new cell contents.
const COLUMN_MIN_WIDTH: Record<string, number> = {
  'Date': 190,
  'Traffic Source': 150,
  'Visiopt Campaign': 170,
  'Ad Campaign': 170,
  'Email': 190,
  'Transaction ID': 170,
  'Sales Amount': 130,
  'Subscription Amount': 170,
  'Reason': 220,
  'Fingerprint': 150,
  'Action': 110,
  'User': 190,
  'Conversions': 120,
  'Goal Name': 220,
};

export function AggregateConversionLog({
  opened,
  onClose,
  params,
}: {
  opened: boolean;
  onClose: () => void;
  params: AggregateConversionLogParams | null;
}) {
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const isDark = theme.colorScheme === 'dark';
  const surfaceBg     = isDark ? theme.colors.dark[6] : theme.colors.gray[0];
  const rowBorder     = isDark ? theme.colors.dark[5] : theme.colors.gray[2];
  const headerLabelCo = isDark ? theme.colors.dark[1] : theme.colors.gray[6];
  const numericCo     = isDark ? theme.colors.gray[3] : theme.colors.gray[8];
  const [goalList, setGoalList] = useState<GoalOption[]>([]);
  const [postbackList, setPostbackList] = useState<GoalOption[]>([]);
  const [webhookList, setWebhookList] = useState<GoalOption[]>([]);

  const [conversionTypes, setConversionTypes] = useState<{ id: string; name: string; kind: string }[]>([]);
  const [conversionType, setConversionType] = useState<string>('');
  const [logKind, setLogKind] = useState<string>('defined');
  const [goalKind, setGoalKind] = useState<string>('goal');
  const [selectedGoalKey, setSelectedGoalKey] = useState<string>('');
  const [applied, setApplied] = useState<{ goalKey: string; convType: string }>({ goalKey: '', convType: '' });

  const [fields, setFields] = useState<string[]>([]);
  /* The Total tab spans every selected campaign, where a subscription amount is not reported, so
     the column is left out there - on screen and in the export alike. */
  const isShownField = (f: string) => !(params?.level === 'total' && f === 'Subscription Amount');
  const visibleFields = fields.filter(isShownField);
  const [rows, setRows] = useState<LogRow[]>([]);
  const [allowMove, setAllowMove] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [attributionLabel, setAttributionLabel] = useState('');
  const [dateRangeLabel, setDateRangeLabel] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [activePage, setActivePage] = useState(1);

  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  // The log is paged on the server, so sort and search are sent with every fetch.
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 400);
  // The search last sent, so a settled debounce never refetches the same query.
  const appliedSearch = useRef('');
  // Paging / searching fast must not let an earlier response overwrite the current one.
  const requestSeq = useRef(0);
  // The table stays mounted across refetches, so a new order is scrolled back to its first row.
  const scrollRef = useRef<HTMLDivElement>(null);

  const kindFromGoalKey = (key: string) => {
    const prefix = key.split(':')[0];
    return prefix === 'postback' || prefix === 'webhook' ? prefix : 'goal';
  };

  const listFor = (kind: string) =>
    kind === 'postback' ? postbackList : kind === 'webhook' ? webhookList : goalList;

  const optionsFor = (kind: string) =>
    listFor(kind).map((g) => ({ value: g.goal_key, label: g.goal_name }));

  // A campaign set can have no goal of a given kind; say so instead of an empty picker.
  const emptyLabelFor = (kind: string) =>
    kind === 'postback'
      ? 'No postbacks on these campaigns'
      : kind === 'webhook'
      ? 'No webhooks on these campaigns'
      : 'No goals on these campaigns';

  const fetchLog = useCallback(
    (goalKey: string, convType: string, page: number, sortState: SortState, searchTerm: string) => {
      if (!params) { return; }
      setLoading(true);
      const seq = ++requestSeq.current;
      appliedSearch.current = searchTerm;
      AggregatedReportsAPI
        .getconversionlog({
          report_id: params.report_id,
          visiopt_campaign_id: params.visiopt_campaign_id,
          filter_visiopt_campaign_ids: params.filter_visiopt_campaign_ids ?? [],
          traffic_source: params.traffic_source ?? [],
          goal_key: goalKey,
          conv_type: convType,
          attribution_model: params.attribution_model,
          attribute_window_type: params.attribute_window_type,
          lookback_days: params.lookback_days,
          report_time: params.report_time,
          level: params.level,
          test_variation_enabled: params.test_variation_enabled ?? 0,
          variation_id: params.variation_id ?? '',
          page,
          number: NUMBER_PER_PAGE,
          sort_field: sortState?.field ?? 'date',
          sort_dir: sortState?.dir ?? 'desc',
          search: searchTerm,
        })
        .then((response) => {
          if (response.ok) { return response.json(); }
          if (response.status === 401 || response.status === 400) {
            navigate('/logout');
          }
          return null;
        })
        .then((data) => {
          if (seq !== requestSeq.current) { return; }
          if (data && data.status === 1 && data.data) {
            setFields(Array.isArray(data.data.conversion_field) ? data.data.conversion_field : []);
            setRows(Array.isArray(data.data.conversion_data) ? data.data.conversion_data : []);
            setAllowMove(data.data.allow_move_to_sub === 1);
            setTotalRecords(data.data.total_records ?? 0);
            setTotalPages(data.data.total_pages ?? 0);
            setAttributionLabel(data.data.attribution_label ?? '');
            setDateRangeLabel(data.data.date_range_label ?? '');
            setLogKind(data.data.log_kind ?? 'defined');
            setApplied({ goalKey, convType });
          } else if (data) {
            notifications.show({
              color: 'red',
              icon: <IconX size="1.1rem" />,
              title: 'Error',
              message: data.message || 'Could not load the conversion log.',
            });
          }
        })
        .catch((e) => console.log(e))
        .finally(() => { if (seq === requestSeq.current) { setLoading(false); } });
    },
    [params, navigate]
  );

  // Identity of what was opened. Keying the seed effect on this - not just the open/close
  // edge - means reopening from a different row always re-seeds, never keeps the last filter.
  const openKey = opened && params
    ? `${params.level}|${params.goal_key}|${params.report_time}|${params.visiopt_campaign_id.join(',')}|${(params.traffic_source ?? []).join(',')}|tv${params.test_variation_enabled ?? 0}|${params.variation_id ?? ''}|${params.initial_kind ?? 'defined'}`
    : '';

  // Opening seeds every filter from the row the user clicked, so the first view matches that number.
  useEffect(() => {
    if (openKey === '' || !params) { return; }
    setRows([]);
    setFields([]);
    setTotalPages(0);
    setTotalRecords(0);
    setActivePage(1);
    setSort(DEFAULT_SORT);
    setApplied({ goalKey: '', convType: '' });
    setSearch('');
    appliedSearch.current = '';
    setGoalList([]);
    setPostbackList([]);
    setWebhookList([]);
    setConversionTypes([]);
    setConversionType('');
    setLogKind('defined');
    setGoalKind(kindFromGoalKey(params.goal_key));
    setSelectedGoalKey(params.goal_key);
    setBusy(true);
    AggregatedReportsAPI
      .getconversionlogdetails({ visiopt_campaign_id: params.visiopt_campaign_id })
      .then((response) => {
        if (response.ok) { return response.json(); }
        if (response.status === 401 || response.status === 400) {
          navigate('/logout');
        }
        return null;
      })
      .then((data) => {
        if (data && data.status === 1 && data.data) {
          const types = Array.isArray(data.data.conversion_type) ? data.data.conversion_type : [];
          setConversionTypes(types);
          setGoalList(Array.isArray(data.data.goal_list) ? data.data.goal_list : []);
          setPostbackList(Array.isArray(data.data.postback_list) ? data.data.postback_list : []);
          setWebhookList(Array.isArray(data.data.webhook_list) ? data.data.webhook_list : []);
          // Defined Conversion is the attributed set the report counts, so it opens on that -
          // unless it was opened from a 0 count that only has undefined ones.
          const wantedKind = params.initial_kind === 'undefined' ? 'undefined' : 'defined';
          const initialType = (types.find((t: any) => t.kind === wantedKind) ?? types[0])?.id ?? '';
          setConversionType(initialType);
          fetchLog(params.goal_key, initialType, 1, DEFAULT_SORT, '');
        }
      })
      .catch((e) => console.log(e))
      .finally(() => setBusy(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openKey]);

  // Typing searches on its own once the input settles, always from the first page.
  useEffect(() => {
    if (openKey === '' || applied.convType === '' || debouncedSearch === appliedSearch.current) { return; }
    setActivePage(1);
    scrollRef.current?.scrollTo({ top: 0 });
    fetchLog(applied.goalKey, applied.convType, 1, sort, debouncedSearch);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const handleKindChange = (kind: string) => {
    setGoalKind(kind);
    const first = optionsFor(kind)[0];
    setSelectedGoalKey(first ? first.value : '');
  };

  // Reset Conversion is a campaign audit list, so it has no goal to pick.
  const activeType = conversionTypes.find((t) => t.id === conversionType);
  const needsGoal = activeType?.kind !== 'reset';

  // Name of the goal the visible rows belong to, so the header tag always matches the table.
  const appliedGoalLabel = applied.goalKey === '' || applied.goalKey === params?.goal_key
    ? (params?.goal_label ?? '')
    : ([...goalList, ...postbackList, ...webhookList].find((g) => g.goal_key === applied.goalKey)?.goal_name ?? '');

  const handleFilter = () => {
    setActivePage(1);
    scrollRef.current?.scrollTo({ top: 0 });
    fetchLog(selectedGoalKey, conversionType, 1, sort, search);
  };

  const handlePageChange = (page: number) => {
    setActivePage(page);
    scrollRef.current?.scrollTo({ top: 0 });
    fetchLog(applied.goalKey, applied.convType, page, sort, search);
  };

  // Same toggle as the report tables: a new column starts ascending, the active one flips.
  const handleSort = (field: string) => {
    const next: SortState = sort && sort.field === field
      ? { field, dir: sort.dir === 'asc' ? 'desc' : 'asc' }
      : { field, dir: 'asc' };
    setSort(next);
    setActivePage(1);
    scrollRef.current?.scrollTo({ top: 0 });
    fetchLog(applied.goalKey, applied.convType, 1, next, search);
  };

  const handleMoveToSubscription = (emailWebhookId: string) => {
    if (!emailWebhookId) { return; }
    setBusy(true);
    TestsApi.movetosubscriptionamount({ webhook_id: emailWebhookId })
      .then((response) => {
        if (response.ok) { return response.json(); }
        if (response.status === 401 || response.status === 400) {
          navigate('/logout');
        }
        return null;
      })
      .then((data) => {
        if (data && data.status === 1) {
          fetchLog(applied.goalKey, applied.convType, activePage, sort, search);
        } else {
          notifications.show({
            color: 'red',
            icon: <IconX size="1.1rem" />,
            title: 'Error',
            message: (data && data.message) || 'Could not move the amount to subscription.',
          });
        }
      })
      .catch((e) => console.log(e))
      .finally(() => setBusy(false));
  };

  // Server pages the table, so export refetches the whole filtered set in one call.
  const handleExport = () => {
    if (!params) { return; }
    setBusy(true);
    AggregatedReportsAPI
      .getconversionlog({
        report_id: params.report_id,
        visiopt_campaign_id: params.visiopt_campaign_id,
        filter_visiopt_campaign_ids: params.filter_visiopt_campaign_ids ?? [],
        traffic_source: params.traffic_source ?? [],
        goal_key: applied.goalKey,
        conv_type: applied.convType,
        attribution_model: params.attribution_model,
        attribute_window_type: params.attribute_window_type,
        lookback_days: params.lookback_days,
        report_time: params.report_time,
        level: params.level,
        test_variation_enabled: params.test_variation_enabled ?? 0,
        variation_id: params.variation_id ?? '',
        page: 1,
        number: NUMBER_PER_PAGE,
        export: 1,
        sort_field: sort?.field ?? 'date',
        sort_dir: sort?.dir ?? 'desc',
        search,
      })
      .then((response) => {
        if (response.ok) { return response.json(); }
        if (response.status === 401 || response.status === 400) {
          navigate('/logout');
        }
        return null;
      })
      .then((data) => {
        const exportRows: LogRow[] = (data && data.data && data.data.conversion_data) || [];
        if (exportRows.length === 0) {
          notifications.show({
            color: 'red',
            icon: <IconX size="1.1rem" />,
            title: 'Error',
            message: 'No data available to export.',
          });
          return;
        }
        const exportFields: string[] = ((data.data.conversion_field as string[]) || []).filter(isShownField);
        const escape = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const csv = [exportFields, ...exportRows.map((r) => exportFields.map((f) => r[FIELD_TO_KEY[f]] ?? ''))]
          .map((row) => row.map(escape).join(','))
          .join('\n');
        const safeName = String(appliedGoalLabel || 'conversion-log')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
        const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${safeName}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      })
      .catch((e) => console.log(e))
      .finally(() => setBusy(false));
  };

  const renderCell = (row: LogRow, field: string) => {
    const value = row[FIELD_TO_KEY[field]] ?? '';
    // Only offer the move when the webhook carries an amount; moving 0.00 changes nothing.
    const movable = parseFloat(row.movable_amount ?? '0') > 0;
    if (field === 'Subscription Amount' && allowMove && value === '0.00' && movable && row.email_webhook_id) {
      return (
        <Button
          size="xs"
          color="teal"
          onClick={() => handleMoveToSubscription(row.email_webhook_id)}
          disabled={busy}
        >
          Move to Subscription
        </Button>
      );
    }
    if (field === 'Traffic Source' || field === 'Visiopt Campaign' || field === 'Ad Campaign') {
      // A conversion can resolve to more than one campaign or ad campaign; show each.
      const items = value.split(', ').filter((v) => v !== '' && v !== '-');
      if (items.length === 0) { return <Text size="sm" color="dimmed">-</Text>; }
      return (
        <Flex gap={6} wrap="wrap">
          {items.map((item) => (
            // The badge clips long ad campaign names, so the full name lives in the tooltip.
            <Tooltip key={item} label={item} withArrow multiline width={320} openDelay={200}>
              <Badge
                size="md"
                variant="light"
                color={field === 'Traffic Source' ? 'blue' : field === 'Visiopt Campaign' ? 'teal' : 'gray'}
                radius="sm"
                sx={{ textTransform: 'none', fontWeight: 600, maxWidth: 260 }}
              >
                {item}
              </Badge>
            </Tooltip>
          ))}
        </Flex>
      );
    }
    if (field === 'Sales Amount' || field === 'Subscription Amount') {
      return (
        <Text size="sm" weight={600} sx={{ color: numericCo, fontVariantNumeric: 'tabular-nums' }}>
          {value}
        </Text>
      );
    }
    if (field === 'Date' || field === 'Transaction ID' || field === 'Fingerprint' || field === 'Conversions') {
      return (
        <Text size="sm" sx={{ color: numericCo, fontVariantNumeric: 'tabular-nums' }}>
          {value === '' ? '-' : value}
        </Text>
      );
    }
    return (
      <Text size="sm">
        {value === '' ? '-' : value}
      </Text>
    );
  };

  return (
    <Modal.Root
      opened={opened}
      onClose={onClose}
      size="85%"
      trapFocus={false}
    >
      <Modal.Overlay />
      <Modal.Content>
        <VisiLoader VisiLoaderVisible={busy} />
        <Modal.Header sx={{ paddingBottom: 12 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Group spacing={8} mb={2} noWrap>
              <Text size="lg" weight={700} sx={{ lineHeight: 1.2 }}>Conversion Log</Text>
              {appliedGoalLabel !== '' && logKind !== 'reset' ? (
                <Badge size="md" variant="light" color="teal" sx={{ textTransform: 'none', fontWeight: 600 }}>
                  {appliedGoalLabel}
                </Badge>
              ) : null}
            </Group>
            {params ? (
              <Text size="sm" color="dimmed" sx={{ wordBreak: 'break-word' }}>
                {logKind === 'defined'
                  ? `${params.scope_label} · attribution and date range follow the report, so these rows match the number you clicked.`
                  : logKind === 'reset'
                  ? `${params.scope_label} · every stats reset recorded against these campaigns.`
                  : logKind === 'record'
                  ? `${params.scope_label} · when this goal switched between all and unique conversions.`
                  : `${params.scope_label} · conversions that arrived but were never credited to this funnel.`}
              </Text>
            ) : null}
          </Box>
          <Modal.CloseButton />
        </Modal.Header>

        <Modal.Body sx={{ paddingTop: 0 }}>
          <Paper
            withBorder
            radius="md"
            p="sm"
            mb="sm"
            sx={{ backgroundColor: surfaceBg, borderColor: rowBorder }}
          >
            <Flex align="center" gap="md" wrap="wrap">
              <Select
                size="sm"
                w={230}
                withinPortal
                value={conversionType}
                onChange={(v) => setConversionType(v ?? '')}
                data={conversionTypes.map((t) => ({ value: t.id, label: t.name }))}
                styles={{ input: { cursor: 'pointer' } }}
              />
              {needsGoal && (
                <>
                  <Text size="sm" weight={600}>Conversion to</Text>
                  <Radio.Group value={goalKind} onChange={handleKindChange}>
                    <Group spacing="lg">
                      <Radio size="sm" value="goal" label="Goals" disabled={goalList.length === 0} />
                      <Radio size="sm" value="postback" label="Postbacks" disabled={postbackList.length === 0} />
                      <Radio size="sm" value="webhook" label="Webhooks" disabled={webhookList.length === 0} />
                    </Group>
                  </Radio.Group>
                  <Select
                    size="sm"
                    searchable
                    withinPortal
                    value={selectedGoalKey}
                    onChange={(v) => setSelectedGoalKey(v ?? '')}
                    data={optionsFor(goalKind)}
                    disabled={listFor(goalKind).length === 0}
                    placeholder={listFor(goalKind).length === 0 ? emptyLabelFor(goalKind) : 'Select'}
                    sx={{ flex: 1, minWidth: 220, maxWidth: 320 }}
                    styles={{ input: { cursor: 'pointer' } }}
                  />
                </>
              )}
              <Button size="sm" color="teal" onClick={handleFilter} disabled={(needsGoal && !selectedGoalKey) || loading}>
                Filter
              </Button>
              <Button
                size="sm"
                variant="default"
                leftIcon={<IconDownload size="0.9rem" />}
                onClick={handleExport}
                disabled={busy || rows.length === 0}
              >
                Export
              </Button>
              {(logKind === 'defined' || logKind === 'undefined') && (
                <TextInput
                  size="sm"
                  w={260}
                  icon={<IconSearch size="0.9rem" />}
                  placeholder="Search email or transaction ID"
                  value={search}
                  onChange={(e) => setSearch(e.currentTarget.value)}
                />
              )}
              <Flex align="center" gap="xs" sx={{ marginLeft: 'auto' }}>
                {attributionLabel !== '' && (
                  <Badge size="md" variant="light" color="teal" radius="sm" sx={{ textTransform: 'none', fontWeight: 600 }}>
                    {attributionLabel}
                  </Badge>
                )}
                {dateRangeLabel !== '' && (
                  <Badge size="md" variant="light" color="blue" radius="sm" sx={{ textTransform: 'none', fontWeight: 600 }}>
                    {dateRangeLabel}
                  </Badge>
                )}
                <Text size="sm" color="dimmed" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {totalRecords.toLocaleString()} conversion{totalRecords === 1 ? '' : 's'}
                </Text>
              </Flex>
            </Flex>
          </Paper>

          <Paper
            withBorder
            radius="md"
            sx={{ backgroundColor: 'transparent', borderColor: rowBorder, overflow: 'hidden', position: 'relative' }}
          >
            {/* Refetches keep the table in place under a light overlay; skeletons are for the first load only. */}
            <LoadingOverlay
              visible={loading && fields.length > 0}
              overlayBlur={1}
              loaderProps={{ variant: 'bars', color: 'gray', size: 'sm' }}
            />
            {loading && fields.length === 0 ? (
              <Flex direction="column" gap="xs" p="sm">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} height={38} radius="sm" />
                ))}
              </Flex>
            ) : (
              <SimpleBar autoHide={false} style={{ maxHeight: 460 }} scrollableNodeProps={{ ref: scrollRef }}>
                {rows.length === 0 ? (
                  <Flex direction="column" align="center" justify="center" py={48}>
                    <IconReceipt size="2rem" stroke={1.4} color={headerLabelCo} />
                    <Text size="sm" mt={8} color="dimmed">
                      {search.trim() !== '' ? 'No conversions match your search.' : 'No conversions match the current filters.'}
                    </Text>
                  </Flex>
                ) : (
                  <Table
                    withColumnBorders
                    sx={{
                      marginBottom: 0,
                      'th, td': { borderColor: `${rowBorder} !important` },
                    }}
                  >
                    <thead style={{ position: 'sticky', top: 0, backgroundColor: surfaceBg, zIndex: 1 }}>
                      <tr>
                        {visibleFields.map((f) => (
                          <SortableTh
                            key={f}
                            field={FIELD_TO_KEY[f] ?? f}
                            sort={sort}
                            onToggle={handleSort}
                            reserveArrow
                            thStyle={{ padding: '10px 12px', whiteSpace: 'nowrap', minWidth: COLUMN_MIN_WIDTH[f] }}
                          >
                            {f}
                          </SortableTh>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, i) => (
                        <tr key={`${row.transaction_id}-${i}`} style={{ borderTop: `1px solid ${rowBorder}`, verticalAlign: 'top' }}>
                          {visibleFields.map((f) => (
                            <td key={f} style={{ padding: '10px 12px', whiteSpace: f === 'Date' ? 'nowrap' : 'normal' }}>
                              {renderCell(row, f)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </SimpleBar>
            )}
          </Paper>

          {totalPages > 1 && (
            <Flex justify="center" mt="md">
              <Pagination.Root total={totalPages} value={activePage} onChange={handlePageChange}>
                <Group spacing={5}>
                  <Pagination.First />
                  <Pagination.Previous />
                  <Pagination.Items />
                  <Pagination.Next />
                  <Pagination.Last />
                </Group>
              </Pagination.Root>
            </Flex>
          )}
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
}
